import * as XLSX from 'xlsx';
import type {
  ShareLinkAnalyticsResponse,
  ShareLinkSeriesPoint,
} from '../../../services/api';
import { saveBlobDownload } from '../../../../shared/utils/saveBlobDownload';

// --- Colour -------------------------------------------------------------------
//
// Validated with the dataviz palette checker against this app's surfaces
// (dark #1a202c, light #ffffff): lightness band, chroma, CVD and normal-vision
// separation all pass for links/leads/opens as a set. Light-mode leads green
// sits under 3:1, so every chart that uses it also prints values (tip labels)
// and has a table view.

export interface ShareVizPalette {
  links: string;
  leads: string;
  opens: string;
  grid: string;
  axis: string;
  /** Faint wash behind the selected row of a ranking chart. */
  selection: string;
}

const DARK: ShareVizPalette = {
  links: '#d95926',
  leads: '#199e70',
  opens: '#3987e5',
  grid: 'rgba(255,255,255,0.07)',
  axis: 'rgba(255,255,255,0.18)',
  selection: 'rgba(255,255,255,0.05)',
};

const LIGHT: ShareVizPalette = {
  links: '#eb6834',
  leads: '#1baf7a',
  opens: '#2a78d6',
  grid: 'rgba(31,41,68,0.08)',
  axis: 'rgba(31,41,68,0.22)',
  selection: 'rgba(31,41,68,0.05)',
};

export const shareVizPalette = (mode: 'light' | 'dark'): ShareVizPalette => (mode === 'dark' ? DARK : LIGHT);

export type ShareMetric = 'links' | 'leads';

export const METRIC_LABEL: Record<ShareMetric, string> = {
  links: 'Links shared',
  leads: 'Leads received',
};

// --- Formatting ---------------------------------------------------------------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** `YYYY-MM-DD` as a UTC date, so weekday / month maths never shift with the browser zone. */
function keyToUtc(key: string): Date {
  return new Date(`${key}T00:00:00Z`);
}

function utcToKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export const formatDayShort = (key: string): string => {
  const d = keyToUtc(key);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

export const formatDayLong = (key: string): string => {
  const d = keyToUtc(key);
  return `${WEEKDAYS[d.getUTCDay()]}, ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

export const formatRangeShort = (start: string, end: string): string => (
  start === end ? formatDayShort(start) : `${formatDayShort(start)} – ${formatDayShort(end)}`
);

export const formatInt = (value: number): string => value.toLocaleString('en-SG');

export const formatPercent = (value: number | null): string => (
  value === null || !Number.isFinite(value) ? '—' : `${(value * 100).toFixed(value >= 0.995 || value === 0 ? 0 : 1)}%`
);

export const ratio = (num: number, den: number): number | null => (den > 0 ? num / den : null);

/** "3h ago" / "2d ago" / "8 Oct" for a timestamp in a table cell. */
export function formatRelative(isoValue: string | null): string {
  if (!isoValue) return '—';
  const ms = Date.parse(isoValue);
  if (Number.isNaN(ms)) return '—';
  const minutes = Math.round((Date.now() - ms) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ms).toLocaleDateString('en-SG', { day: 'numeric', month: 'short' });
}

export function formatTimestamp(isoValue: string | null): string {
  if (!isoValue) return '';
  const d = new Date(isoValue);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-SG', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// --- Time buckets -------------------------------------------------------------

export type TrendView = 'daily' | 'weekly' | 'monthly' | 'cumulative';

export interface TrendBucket {
  key: string;
  /** X-axis tick. */
  label: string;
  /** Tooltip / table heading. */
  title: string;
  links: number;
  leads: number;
  verificationsStarted: number;
  verificationsAbandoned: number;
  /** Distinct agents sharing — daily view only (it doesn't sum across days). */
  sharers: number | null;
}

function weekStartKey(key: string): string {
  const d = keyToUtc(key);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7)); // back to Monday
  return utcToKey(d);
}

function emptyBucket(key: string, label: string, title: string): TrendBucket {
  return { key, label, title, links: 0, leads: 0, verificationsStarted: 0, verificationsAbandoned: 0, sharers: null };
}

/** Group the daily series by week / month, or carry running totals for the cumulative view. */
export function bucketSeries(series: ShareLinkSeriesPoint[], view: TrendView): TrendBucket[] {
  if (view === 'daily' || view === 'cumulative') {
    let links = 0;
    let leads = 0;
    let started = 0;
    let abandoned = 0;
    return series.map((p) => {
      links += p.links;
      leads += p.leads;
      started += p.verificationsStarted;
      abandoned += p.verificationsAbandoned;
      const cumulative = view === 'cumulative';
      return {
        key: p.date,
        label: formatDayShort(p.date),
        title: formatDayLong(p.date),
        links: cumulative ? links : p.links,
        leads: cumulative ? leads : p.leads,
        verificationsStarted: cumulative ? started : p.verificationsStarted,
        verificationsAbandoned: cumulative ? abandoned : p.verificationsAbandoned,
        sharers: cumulative ? null : p.sharers,
      };
    });
  }

  const buckets = new Map<string, TrendBucket>();
  for (const p of series) {
    let key: string;
    let bucket: TrendBucket | undefined;
    if (view === 'weekly') {
      key = weekStartKey(p.date);
      bucket = buckets.get(key) ?? emptyBucket(key, formatDayShort(key), `Week of ${formatDayLong(key)}`);
    } else {
      key = p.date.slice(0, 7);
      const d = keyToUtc(`${key}-01`);
      const name = `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
      bucket = buckets.get(key) ?? emptyBucket(key, name, name);
    }
    bucket.links += p.links;
    bucket.leads += p.leads;
    bucket.verificationsStarted += p.verificationsStarted;
    bucket.verificationsAbandoned += p.verificationsAbandoned;
    buckets.set(key, bucket);
  }
  return Array.from(buckets.values());
}

export const VIEW_UNIT: Record<TrendView, string> = {
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
  cumulative: 'day',
};

// --- Export -------------------------------------------------------------------

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * One workbook with every view of the dashboard, for offline analysis. `scope`
 * names a team or project view in the summary sheet and the file name.
 */
export async function downloadShareLinksWorkbook(
  data: ShareLinkAnalyticsResponse,
  scope?: { label: string; fileTag: string },
): Promise<void> {
  const agentName = new Map(data.agents.map((a) => [a.ceaNumber, a.name]));
  const projectName = new Map(data.projects.map((p) => [p.projectId, p.projectName]));
  const { totals, previousTotals, range, previousRange } = data;

  const summary = XLSX.utils.aoa_to_sheet([
    ['Share Links analytics', `${range.startDate} to ${range.endDate}`],
    ...(scope ? [['Scope', scope.label]] : []),
    ['Compared with', `${previousRange.startDate} to ${previousRange.endDate}`],
    ['Possible agents', data.excludePossibleAgents
      ? `Excluded (${data.possibleAgentLeads} leads left out)`
      : `Included (${data.possibleAgentLeads} leads)`],
    [],
    ['Metric', 'This period', 'Previous period'],
    ['Links shared', totals.links, previousTotals.links],
    ['Agents sharing', totals.sharers, previousTotals.sharers],
    ['Projects shared', totals.projects, previousTotals.projects],
    ['Leads received', totals.leads, previousTotals.leads],
    ['Unique lead mobiles', totals.uniqueLeadMobiles, previousTotals.uniqueLeadMobiles],
    ['Verifications started', totals.verificationsStarted, previousTotals.verificationsStarted],
    ['Verifications completed', totals.verificationsCompleted, previousTotals.verificationsCompleted],
    ['Opens (all-time, on links shared this period)', totals.opens, ''],
    ['Links revoked', totals.revokedLinks, ''],
    [],
    ['Links count by share date; leads by the date the buyer verified on WhatsApp (on any link).'],
    ['Opens are only kept as an all-time total per link.'],
  ]);

  const daily = XLSX.utils.json_to_sheet(data.series.map((p) => ({
    Date: p.date,
    'Links shared': p.links,
    'Agents sharing': p.sharers,
    'Leads received': p.leads,
    'Verifications started': p.verificationsStarted,
    'Verifications not finished': p.verificationsAbandoned,
  })));

  const projects = XLSX.utils.json_to_sheet(data.projects.map((p, i) => ({
    Rank: i + 1,
    Project: p.projectName,
    'Project ID': p.projectId,
    'Links shared': p.links,
    'Agents sharing': p.sharers,
    'Leads received': p.leads,
    'Opens (all-time)': p.opens,
    'Last shared': formatTimestamp(p.lastSharedAt),
    'Last lead': formatTimestamp(p.lastLeadAt),
  })));

  const agents = XLSX.utils.json_to_sheet(data.agents.map((a, i) => ({
    Rank: i + 1,
    Agent: a.name,
    'CEA Reg No': a.ceaNumber,
    Mobile: a.mobile,
    'Links shared': a.links,
    'Projects shared': a.projects,
    'Leads received': a.leads,
    'Opens (all-time)': a.opens,
    'Last shared': formatTimestamp(a.lastSharedAt),
    'Last lead': formatTimestamp(a.lastLeadAt),
  })));

  const pairs = XLSX.utils.json_to_sheet(
    [...data.pairs]
      .sort((a, b) => b.links - a.links || b.leads - a.leads)
      .map((p) => ({
        Agent: agentName.get(p.ceaNumber) ?? p.ceaNumber,
        'CEA Reg No': p.ceaNumber,
        Project: projectName.get(p.projectId) ?? p.projectId,
        'Links shared': p.links,
        'Leads received': p.leads,
        'Opens (all-time)': p.opens,
        'Last shared': formatTimestamp(p.lastSharedAt),
        'Last lead': formatTimestamp(p.lastLeadAt),
      })),
  );

  const links = XLSX.utils.json_to_sheet(data.links.map((l) => ({
    'Link ID': l.id,
    Agent: agentName.get(l.ceaNumber) ?? l.ceaNumber,
    'CEA Reg No': l.ceaNumber,
    Project: projectName.get(l.projectId) ?? l.projectId,
    Description: l.description ?? '',
    'Short URL': l.shortUrl ?? '',
    Created: formatTimestamp(l.createdAt),
    'Shared in period': l.sharedInRange ? 'Yes' : 'No',
    'Opens (all-time)': l.opens,
    'Leads in period': l.leadsInRange,
    'Leads (all-time)': l.leadsAllTime,
    'Last lead': formatTimestamp(l.lastLeadAt),
    Revoked: formatTimestamp(l.revokedAt),
  })));

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, summary, 'Summary');
  XLSX.utils.book_append_sheet(book, daily, 'Daily');
  XLSX.utils.book_append_sheet(book, projects, 'Projects');
  XLSX.utils.book_append_sheet(book, agents, 'Sharers');
  XLSX.utils.book_append_sheet(book, pairs, 'Sharer x Project');
  XLSX.utils.book_append_sheet(book, links, 'Links');

  const bytes = XLSX.write(book, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  await saveBlobDownload(
    new Blob([bytes], { type: XLSX_MIME }),
    `share-links-${scope ? `${scope.fileTag}-` : ''}${range.startDate}-to-${range.endDate}.xlsx`,
    XLSX_MIME,
  );
}
